import { isColorScheme, type TColorScheme as ColorScheme } from '@glomopay/utils';
import { useTheme } from 'next-themes';

export {
  APPLIED_COLOR_SCHEME_MESSAGE_TYPE,
  COLOR_SCHEME_MESSAGE_TYPE,
  COLOR_SCHEMES,
  isAppliedColorScheme,
  isColorScheme,
  type TAppliedColorScheme as AppliedColorScheme,
  type TColorScheme as ColorScheme,
} from '@glomopay/utils';

export const COLOR_SCHEME_STORAGE_KEY = 'glomo-color-scheme';

// The init script strips `?colorScheme=` before React mounts and leaves the resolved preference here
// (on `data-requested-color-scheme` when gated), so embedded apps can seed `forcedTheme` from it.
export const readInitialColorScheme = (): ColorScheme | undefined => {
  const root = document.documentElement;
  const value = root.getAttribute('data-requested-color-scheme') ?? root.getAttribute('data-color-scheme');
  return isColorScheme(value) ? value : undefined;
};

// `queryParam`, `storage` and `default` come from the init script; `message` and `disabled` from ThemeProvider.
export type ColorSchemeSource = 'queryParam' | 'storage' | 'default' | 'message' | 'disabled';

export interface ColorSchemeResolution {
  colorScheme: ColorScheme;
  colorSchemeSource: ColorSchemeSource;
}

export const readInitialColorSchemeSource = (): ColorSchemeSource => {
  const value = document.documentElement.getAttribute('data-color-scheme-source');
  return value === 'queryParam' || value === 'storage' ? value : 'default';
};

// Set by the init script when a Glomo embedder vouched that the flag is on, so the scheme can show before preferences load.
export const isInitialColorSchemeConfirmed = () => document.documentElement.hasAttribute('data-color-scheme-confirmed');

export const useColorScheme = (): ColorScheme | undefined => {
  const { forcedTheme, theme } = useTheme();
  const effective = forcedTheme ?? theme;
  return isColorScheme(effective) ? effective : undefined;
};

export const useResolvedColorScheme = () => {
  const { forcedTheme, resolvedTheme } = useTheme();
  return forcedTheme ?? resolvedTheme;
};
